import type { Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import { useMemo } from "react";
import { useMantineColorScheme, useMantineTheme } from "@mantine/core";
import { useLocale } from "./locale";

/**
 * @see https://mtlynch.io/stripe-update/
 */
loadStripe.setLoadParameters({ advancedFraudSignals: false });

let stripePromise: Promise<Stripe | null> | null = null;

export const useStripe = () => {
  const locale = useLocale();
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();

  const stripe: Promise<Stripe | null> = useMemo(async () => {
    return stripePromise ?? (stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, {
      betas: ["elements_tax_id_1"],
    }));
  }, []);

  /**
   * Stripe Elements appearance configuration
   * Following Mapka UI Kit design system
   * @see https://www.figma.com/design/SGgCOChfc92PZwp5EqMEN0/MAPKA-UI-Kit?node-id=90-51147&m=dev
   */
  const options: StripeElementsOptions = useMemo(() => ({
    mode: "setup" as const,
    currency: "usd",
    locale: locale === "hi"
      ? "auto"
      : locale,
    loader: "always",
    appearance: colorScheme === "dark"
      ? {
          theme: "night",
          disableAnimations: true,
          rules: {
            ".Label": {
              fontSize: "14px",
              fontWeight: "600",
              color: "white",
            },
            ".Input": {
              color: "white",
              borderColor: "white",
              borderRadius: "8px",
              borderWidth: "2px",
              backgroundColor: "rgb(46, 46, 46)",
              padding: "8px",
              transition: "border-color 0.15s ease",
            },
            ".Input:focus": {
              borderColor: theme.colors.orange[9],
              boxShadow: "none",
              outline: "none",
            },
            ".Input--invalid": {
              borderColor: "#ff6b6b",
            },
            ".Input::placeholder": {
              color: "#868e96",
            },
            ".Error": {
              color: "#ff6b6b",
              fontSize: "14px",
            },
          },
          variables: {
            fontFamily: "Geist, sans-serif",
            colorPrimary: "white",
            colorDanger: "#ff6b6b",
            fontSizeBase: "14px",
            borderRadius: "8px",
          },
        }
      : {
          theme: "stripe",
          disableAnimations: true,
          rules: {
            ".Label": {
              fontSize: "14px",
              fontWeight: "500",
              color: "black",
            },
            ".Input": {
              borderColor: "black",
              color: "black",
              padding: "8px",
              borderRadius: "8px",
              borderWidth: "2px",
              backgroundColor: "white",
              transition: "border-color 0.15s ease",
            },
            ".Input:focus": {
              borderColor: theme.colors.orange[9],
              boxShadow: "none",
              outline: "none",
            },
            ".Input--invalid": {
              borderColor: "#ff6b6b",
            },
            ".Input::placeholder": {
              color: "#868e96",
            },
            ".Error": {
              color: "#ff6b6b",
              fontSize: "14px",
            },
          },
          variables: {
            fontFamily: "Geist, sans-serif",
            colorPrimary: "black",
            colorDanger: "#ff6b6b",
            fontSizeBase: "14px",
            borderRadius: "8px",
          },
        },
    payment_method_types: ["card"],
  }), [locale, colorScheme]);

  return useMemo(() => ({
    stripe,
    options,
  }), [stripe, options]);
};
